import DateRangerPicker from "components/date-range-picker";
import { SelectBox } from "components/uikit-adapter/SelectBoxTyped";
import moment from "moment";
import intl from "react-intl-universal";
import { ISO_DATE_FORMAT } from "utils/format-date";
import type { ProductionPlanSupplierFilters, ProductionPlanSupplierOption } from "../../types";
import * as S from "./styled";

type DateRangeField = "dataDe" | "dataAte";

interface ProductionPlanSupplierFilterFieldsProps {
  readonly filters: ProductionPlanSupplierFilters;
  readonly supplierOptions: ProductionPlanSupplierOption[];
  readonly materialOptions: ProductionPlanSupplierOption[];
  readonly familyOptions: ProductionPlanSupplierOption[];
  readonly labeledFamilyOptions: ProductionPlanSupplierOption[];
  onChange: <T extends keyof ProductionPlanSupplierFilters>(
    field: T,
    value: ProductionPlanSupplierFilters[T]
  ) => void;
  readonly filterOptionsLoading: boolean;
}

const ProductionPlanSupplierFilterFields = ({
  filters,
  supplierOptions,
  materialOptions,
  familyOptions,
  labeledFamilyOptions,
  onChange,
  filterOptionsLoading,
}: ProductionPlanSupplierFilterFieldsProps) => {
  const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;

  const selectMenuStyles = {
    menu: (provided: Record<string, unknown>) => ({
      ...provided,
      position: "absolute",
      zIndex: 9999,
    }),
    menuPortal: (
      provided: Record<string, unknown>,
      state: { rect: { left: number; width: number } }
    ) => {
      const viewportWidth = typeof window === "undefined" ? state.rect.width * 2 : window.innerWidth;
      const horizontalGutter = 8;
      const maxWidth = Math.max(viewportWidth - horizontalGutter * 2, state.rect.width);
      const width = Math.min(Math.max(state.rect.width * 2, 320), maxWidth);
      const left = Math.max(
        horizontalGutter,
        Math.min(state.rect.left, viewportWidth - width - horizontalGutter)
      );

      return {
        ...provided,
        left,
        width,
        zIndex: 9999,
      };
    },
    option: (
      provided: Record<string, unknown>,
      state: { isDisabled?: boolean; isSelected?: boolean }
    ) => ({
      ...provided,
      height: 37,
      display: "flex",
      alignItems: "center",
      paddingLeft: 20,
      color: state.isDisabled ? "gray" : "#333",
      fontSize: "13px",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: state.isSelected ? "var(--color-action-hover)" : "white",
      whiteSpace: "nowrap",
      "&:hover": {
        backgroundColor: "var(--color-neutral-300)",
      },
    }),
  };
  const selectMenuListStyle = {
    overflowX: "auto",
    "::-webkit-scrollbar": {
      width: "4px",
      height: "4px",
      backgroundColor: "#E0E0E0",
    },
    "::-webkit-scrollbar-thumb": {
      borderRadius: "2px",
      boxShadow: "inset 0 0 6px rgba(0, 0, 0, .3)",
      backgroundColor: "#BDBDBD",
    },
  };

  const handleDateRangeChange = (field: DateRangeField, value: string | null | undefined) => {
    onChange(field === "dataDe" ? "StartDate" : "EndDate", value ?? "");
  };

  return (
    <S.FiltersWrapper>
      <S.FieldWrapper>
        <SelectBox
          name="Suppliers"
          label={intl.get("bottles.suppliers")}
          placeholder={intl.get("bottles.suppliers")}
          valueKey="Id"
          labelKey="Description"
          multi
          searchable
          clearable
          disabled={filterOptionsLoading}
          value={filters.Suppliers}
          onChange={(value) => onChange("Suppliers", value ?? [])}
          options={supplierOptions}
          styles={selectMenuStyles}
          menuListStyle={selectMenuListStyle}
          menuPortalTarget={menuPortalTarget}
        />
      </S.FieldWrapper>

      <S.FieldWrapper>
        <SelectBox
          name="Materials"
          label={intl.get("commons.material")}
          placeholder={intl.get("commons.material")}
          valueKey="Id"
          labelKey="Description"
          multi
          searchable
          clearable
          disabled={filterOptionsLoading}
          value={filters.Materials}
          onChange={(value) => onChange("Materials", value ?? [])}
          options={materialOptions}
          styles={selectMenuStyles}
          menuListStyle={selectMenuListStyle}
          menuPortalTarget={menuPortalTarget}
        />
      </S.FieldWrapper>

      <S.FieldWrapper>
        <SelectBox
          name="Families"
          label={intl.get("commons.family")}
          placeholder={intl.get("commons.family")}
          valueKey="Id"
          labelKey="Description"
          multi
          searchable
          clearable
          disabled={filterOptionsLoading}
          value={filters.Families}
          onChange={(value) => onChange("Families", value ?? [])}
          options={familyOptions}
          styles={selectMenuStyles}
          menuListStyle={selectMenuListStyle}
          menuPortalTarget={menuPortalTarget}
        />
      </S.FieldWrapper>

      <S.FieldWrapper>
        <SelectBox
          name="LabeledFamilies"
          label={intl.get("commons.labeledFamily")}
          placeholder={intl.get("commons.labeledFamily")}
          valueKey="Id"
          labelKey="Description"
          multi
          searchable
          clearable
          disabled={filterOptionsLoading}
          value={filters.LabeledFamilies}
          onChange={(value) => onChange("LabeledFamilies", value ?? [])}
          options={labeledFamilyOptions}
          styles={selectMenuStyles}
          menuListStyle={selectMenuListStyle}
          menuPortalTarget={menuPortalTarget}
        />
      </S.FieldWrapper>

      <S.DateRangeWrapper>
        <DateRangerPicker
          title={intl.get("commons.period")}
          dataDe={filters.StartDate}
          dataAte={filters.EndDate}
          dataMin={
            filters.EndDate
              ? moment(filters.EndDate).add(-29, "days").format(ISO_DATE_FORMAT)
              : undefined
          }
          maxDate={
            filters.StartDate
              ? moment(filters.StartDate).add(29, "days").format(ISO_DATE_FORMAT)
              : undefined
          }
          handleChange={handleDateRangeChange}
        />
      </S.DateRangeWrapper>
    </S.FiltersWrapper>
  );
};

export default ProductionPlanSupplierFilterFields;
